import "./home.scss";

import MethodologiesCards from "@frontend/js/Components/Shared/MethodologiesCards/MethodologiesCards";
import PublicationsCards from "@frontend/js/Components/Site/PublicationsCards/PublicationsCards";
import CaseStudiesCards from "@frontend/js/Components/Site/CaseStudiesCards/CaseStudiesCards";
import PartnersSlider from "@frontend/js/Components/Site/PartnersSlider/PartnersSlider";
import ServicesCards from "@frontend/js/Components/Site/ServicesCards/ServicesCards";
import CareerOffers from "@frontend/js/Components/Site/CareerOffers/CareerOffers";
import MetaHead from "@frontend/js/Components/Shared/MetaHead/MetaHead";
import ArrowNextBigIcon from "@frontend/images/icons/ArrowNextBigIcon";
import ArrowNextIcon from "@frontend/images/icons/ArrowNextIcon";
import Form from "@frontend/js/Components/Shared/Form/Form";
import Hero from "@frontend/js/Components/Site/Hero/Hero";
import SiteLayout from "@frontend/js/Layouts/SiteLayout";
import { Link, usePage } from "@inertiajs/react";
import useT from "@frontend/js/hooks/useT";
import { useState } from "react";

export default function Home({
    meta,
    serviceCategories = [],
    servicesWithoutCategory = [],
    caseStudies = [],
    articles = [],
    blocks = [],
    methodologies = [],
    servicesPageTitle = "",
    methodologiesPageTitle = "",
    methodologiesPageHref = "",
    caseStudiesPageTitle = "",
    caseStudiesPageHref = "",
    vacanciesPageTitle = "",
    vacanciesPageHref = "",
    vacanciesCount = 0,
    articlesPageTitle = "",
    articlesPageHref = "",
}) {
    const { t } = useT("site");
    const pageProps = usePage().props;
    const locale = pageProps?.currentLocale || "ro";
    const [isHeroFormOpen, setIsHeroFormOpen] = useState(false);
    const title = "";
    const serviceCategoriesData = serviceCategories?.data || [];
    const servicesWithoutCategoryData = servicesWithoutCategory?.data || [];
    const caseStudiesData = caseStudies?.data || [];
    const articlesData = articles?.data || [];
    const methodologiesData = methodologies?.data || [];
    const offerBlocks = [
        blocks?.data?.["vacancy-offer-block-1"],
        blocks?.data?.["vacancy-offer-block-2"],
        blocks?.data?.["vacancy-offer-block-3"],
        blocks?.data?.["vacancy-offer-block-4"],
    ].filter(Boolean);
    const homeTitle = title ? `${title}__NO_APP_SUFFIX__` : "__NO_APP_SUFFIX__";

    async function handleSubmit(
        formData,
        { reset, clearErrors, setError, source, sourceId },
    ) {
        try {
            const csrfToken = document
                .querySelector('meta[name="csrf-token"]')
                ?.getAttribute("content");

            const response = await fetch("/contact-form", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                    "X-CSRF-TOKEN": csrfToken || "",
                },
                body: JSON.stringify({
                    ...formData,
                    locale,
                    source: source || 1,
                    ...(sourceId && { sourceId }),
                }),
            });

            const text = await response.text();

            if (response.status === 422) {
                const result = JSON.parse(text);
                if (result.errors) {
                    Object.entries(result.errors).forEach(
                        ([field, messages]) => {
                            setError(field, messages[0]);
                        },
                    );
                }
                return { validationErrors: true };
            }

            if (!response.ok) {
                console.error("Form submission error:", text);
                return false;
            }

            const result = JSON.parse(text);

            if (!result.success) {
                return false;
            }

            reset();
            return true;
        } catch (error) {
            console.error("Form submission error:", error);
            return false;
        }
    }

    return (
        <>
            <MetaHead meta={meta} />
            <SiteLayout>
                <div className="home">
                    <Hero onCtaClick={() => setIsHeroFormOpen(true)} />
                    <Form
                        variant="drawer"
                        isOpen={isHeroFormOpen}
                        onClose={() => setIsHeroFormOpen(false)}
                        source={1}
                        onSubmit={handleSubmit}
                    />
                    <div className="home__container">
                        <div className="home__container__services">
                            <div className="home__container__services__title">
                                <h2>{servicesPageTitle}</h2>
                                <p>{t("main-page-services-description")}</p>
                            </div>
                            <div className="servicesCards">
                                <ServicesCards
                                    cards={serviceCategoriesData}
                                    href={(card) => card.href}
                                />
                                {servicesWithoutCategoryData.length > 0 && (
                                    <ServicesCards
                                        cards={servicesWithoutCategoryData}
                                        hideLabel={true}
                                        href={(card) => card.href}
                                    />
                                )}
                            </div>
                        </div>
                        <div className="home__container__methodologies">
                            <div className="home__container__methodologies__title">
                                <h2>{methodologiesPageTitle}</h2>
                                <p>
                                    {t("main-page-methodologies-description")}
                                </p>
                                <Link
                                    href={methodologiesPageHref}
                                    className="home__container__methodologies__title__linkButton"
                                >
                                    {t("methodologies-show-more-button")}{" "}
                                    <ArrowNextIcon />
                                </Link>
                            </div>
                            <div className="home__container__methodologies__cards">
                                <MethodologiesCards
                                    stackOnScroll
                                    cards={methodologiesData}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="home__caseStudies">
                        <div className="home__caseStudies__inner">
                            <div className="home__caseStudies__title">
                                <Link
                                    href={caseStudiesPageHref}
                                    className="home__caseStudies__title__link"
                                >
                                    <div className="home__caseStudies__title__content">
                                        <h2>{caseStudiesPageTitle}</h2>
                                        <p>
                                            {t(
                                                "main-page-case-studies-description",
                                            )}
                                        </p>
                                    </div>
                                    <span
                                        className="home__animatedArrow"
                                        aria-hidden="true"
                                    >
                                        <span className="home__animatedArrow__icon home__animatedArrow__icon--first">
                                            <ArrowNextBigIcon />
                                        </span>
                                        <span className="home__animatedArrow__icon home__animatedArrow__icon--second">
                                            <ArrowNextBigIcon />
                                        </span>
                                    </span>
                                </Link>
                            </div>
                            <CaseStudiesCards
                                cardModifier="white"
                                cards={caseStudiesData}
                            />
                        </div>
                    </div>
                    <div className="home__cariers">
                        <div className="home__cariers__inner">
                            <div className="home__cariers__title">
                                <h2>{vacanciesPageTitle}</h2>
                                <p>{t("main-page-vacancies-description")}</p>
                            </div>
                            <CareerOffers
                                showVacanciesButton
                                showSliderControls
                                fullWidthMobile
                                offerBlocks={offerBlocks}
                                vacanciesPageHref={vacanciesPageHref}
                                vacanciesCount={vacanciesCount}
                            />
                        </div>
                    </div>
                    <div className="home__container">
                        <div className="home__container__publications">
                            <div className="home__container__publications__title">
                                <Link
                                    href={articlesPageHref}
                                    className="home__container__publications__title__link"
                                >
                                    <div className="home__container__publications__title__content">
                                        <h2>{articlesPageTitle}</h2>
                                        <p>
                                            {t(
                                                "main-page-articles-description",
                                            )}
                                        </p>
                                    </div>
                                    <span
                                        className="home__animatedArrow"
                                        aria-hidden="true"
                                    >
                                        <span className="home__animatedArrow__icon home__animatedArrow__icon--first">
                                            <ArrowNextBigIcon />
                                        </span>
                                        <span className="home__animatedArrow__icon home__animatedArrow__icon--second">
                                            <ArrowNextBigIcon />
                                        </span>
                                    </span>
                                </Link>
                            </div>
                            <PublicationsCards cards={articles} />
                        </div>
                        <PartnersSlider />
                    </div>
                </div>
            </SiteLayout>
        </>
    );
}
